import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchApi } from '../utils/fetchApi';
import { useBuilding } from '../context/buildingContext';
import { useUser } from '../context/userContext';

interface Message {
  id: number;
  title: string;
  body: string;
  created_at: string;
  scope_type: string;
  building_name: string | null;
  building_id: number | null;
  apartment_id: number | null;
  building_address: string | null;
  apartment_name: string | null;
  is_read: boolean;
}

const formatDate = (dateStr: string): string => {
  const normalized = dateStr.replace(' ', 'T');
  const date = new Date(normalized);
  return date.toLocaleDateString('hu-HU', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const MAX_MESSAGES = 20;
const THREE_MONTHS_MS = 3 * 30 * 24 * 60 * 60 * 1000;

const filterMessages = (data: Message[]): Message[] => {
  const cutoff = Date.now() - THREE_MONTHS_MS;
  return data
    .filter(m => new Date(m.created_at.replace(' ', 'T')).getTime() >= cutoff)
    .slice(0, MAX_MESSAGES);
};

const Messages: React.FC = () => {
  const { t } = useTranslation();
  const { apartmentId, buildingId } = useBuilding();
  const { setHasUnreadMessages } = useUser();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadMessages = async () => {
      try {
        const payload = apartmentId
          ? { apartment_ids: [parseInt(apartmentId)], building_ids: [parseInt(buildingId ?? "0")]  }
          : {};
        const data: Message[] = await fetchApi('get_messages', payload);
        const filtered = filterMessages(data);
        setMessages(filtered);

        const unread = filtered.filter((m) => !m.is_read);
        setHasUnreadMessages(unread.length > 0);

        if (unread.length > 0) {
          await Promise.all(
            unread.map((m) => fetchApi('mark_message_as_read', { message_id: m.id }))
          );
          setHasUnreadMessages(false);
        }
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadMessages();
  }, [apartmentId, buildingId, setHasUnreadMessages]);

  return (
    <div className="messagesPage">
      <div className="pageHeader">
        <h3>{t('messages')}</h3>
      </div>
      {loading ? (
        <div className="messagesLoading">{t('loading')}</div>
      ) : error ? (
        <div className="messagesLoading">{t('error.loadingFailed')}</div>
      ) : (
        <div className="messagesList">
          {messages.map((msg) => (
            <div key={msg.id} className="messageCard">
              {!msg.is_read && <div className="messageDot messageDot--unread" />}
              <div className="messageContent">
                <p className="messageTitle">{msg.title}</p>
                <p className="messageText">{msg.body}</p>
                <span className="messageDate">{formatDate(msg.created_at)}</span>
              </div>
            </div>
          ))}
          {messages.length > 0 && (
            <p className="messagesLimitNote">{t('messagesLimit', { count: MAX_MESSAGES })}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default Messages;
